import React from "react";
import {connect} from "react-redux";
import DataComponent from "Core/components/DataComponent";
import {getArray, isset} from "Core/helpers/data";
import {getGlobalActions} from "Core/helpers/redux";
import * as actions from "./actions";
import {isSuccessful} from "Core/helpers/io";
import SelectInput from "Core/components/input/SelectInput";
import {orderBy, filter} from "lodash";
import PropTypes from "prop-types";
import {AsyncMountError} from "Core/errors";

class StudyStatusSelectInput extends DataComponent {
	constructor(props) {
		super(props, {
			data: {
				/**
				 * List of loaded statuses
				 * @type {Object[]}
				 */
				statuses: [],
				/**
				 * Flag that shows if statuses are being loaded
				 * @type {boolean}
				 */
				statusesLoading: false,
			}
		}, {
			disableLoad: true,
		});
	}

	/**
	 * Replacement for default 'componentDidMount' method that will return a promise
	 * @note This method should be used instead of the default 'componentDidMount' when you need to have async calls in
	 * your 'componentDidMount'.
	 * @important Please do not forget to decrease the value of this.mountCount once async calls finish.
	 *
	 * @param {boolean} [override=false] - Flag that determines if this method should be executed in the 'override' mode.
	 * @note Override mode is reserved for calls by the child 'componentDidMount' methods that override this method to
	 * enable overriding the data loading functionality but still executing the base component's 'componentDidMount' that
	 * handles core functionality like adding registered event listeners.
	 * @return {Promise<number|undefined>} Promise that will resolve with the updated mount count that
	 * will be set in the 'componentDidMount' method or undefined for default functionality where 'componentDidMount'
	 * will just reset the mount count to zero.
	 * @throws {AsyncMountError} Promise can reject with the AsyncMountError in which case another
	 * 'asyncComponentDidMount' will be called if mount count is greater than zero.
	 */
	async asyncComponentDidMount(override = false) {
		// Call the parent component's 'asyncComponentDidMount' method that handles core functionality
		await super.asyncComponentDidMount(override);

		const {options, fetchStudyStatusesAction} = this.props;

		if (!isset(options)) {
			await this.setValue('statusesLoading', true)
				.then(() => this.executeAbortableAction(fetchStudyStatusesAction))
				.then(response => {
					if (!isset(response)) {
						throw new AsyncMountError();
					} else if (isSuccessful(response)) {
						return this.setValue('statuses',
							orderBy(
								getArray(response, 'data').map(s => ({
									...s,
									// Use i18n to get the status name used as label but fallback to the study status name field
									name: this.hasTranslation(s.code, 'constants.studyStatus') ?
										this.t(s.code, 'constants.studyStatus') :
										s.name
								})),
								['ordinal']
							)
						)
							.then(() => this.setValue('statusesLoading', false))
					} else if (isset(response)) {
						return this.setValue('statusesLoading', false);
					}
				})
				.catch(async (error) => {
					await this.setValue('statusesLoading', false);

					if (error instanceof AsyncMountError) throw error;
					else return Promise.resolve();
				});
		} else {
			await this.setValue('statuses', getArray(options));
		}

		return Promise.resolve();
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {forFilter} = this.props;
		
		// Get study statuses used as options
		const options = (
			forFilter ?
				// If select is used in the filter, only get study statuses having 'includeInFilter' flag set to true 
				filter(getArray(this.getValue('statuses')), {includeInFilter: true}) :
				// If select is used regularly, get all study statuses
				getArray(this.getValue('statuses'))
		);
		
		return (
			<SelectInput
				primaryKey="id"
				options={options}
				isLoading={this.getValue('statusesLoading')}
				getOptionLabel={o => o.name}
				getOptionValue={o => o.id}
				{...this.props}
			/>
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
StudyStatusSelectInput.propTypes = {
	...SelectInput.propTypes,
	
	forFilter: PropTypes.bool,
};

/**
 * Define component default values for own props
 */
StudyStatusSelectInput.defaultProps = {
	forFilter: false,
};

export default connect(null, getGlobalActions(actions))(StudyStatusSelectInput);